import React, { FC } from "react";
import {
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CustomBadge } from "../../../shared/CustomBadge/CustomBadge";
import Typhography from "../../../shared/Typhography/Typhography";
import style from "../ProfilingProtocolDetailPage.module.scss";

interface ILoadVelocityChartProps {
  inputs: any[];
  outputs: Record<string, any>;
}

const TICK_STYLE = {
  fill: "#4d4d4d",
  fontSize: 11,
  fontFamily: "'Satoshi', sans-serif",
};

const CustomTooltipContent: FC<any> = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload ?? {};
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e7e7e7",
        borderRadius: 8,
        padding: "8px 12px",
        fontFamily: "'Satoshi', sans-serif",
        fontSize: 12,
        color: "#00212c",
      }}
    >
      <div>Carico: <strong>{d.load} kg</strong></div>
      <div>Velocità: <strong>{d.velocity} m/s</strong></div>
    </div>
  );
};

export const LoadVelocityChart: FC<ILoadVelocityChartProps> = ({
  inputs,
  outputs,
}) => {
  const scatterData = (inputs as any[]).map((i) => ({
    load: Number(i.load_kg ?? i.load),
    velocity: Number(i.velocity_ms ?? i.velocity),
  }));

  const mvt: number | undefined = outputs?.MVT;
  const estimated1RM: number | undefined = outputs?.estimated1RM;
  const r2: number | undefined = outputs?.R2;

  const summaryParts: string[] = [];
  if (estimated1RM != null && mvt != null) {
    summaryParts.push(`1RM stimato: ${estimated1RM} kg @ MVT ${mvt} m/s`);
  }
  if (r2 != null) {
    summaryParts.push(`R²: ${r2}`);
  }
  const summaryLabel = summaryParts.join(" · ");

  return (
    <div className={style.card}>
      <div className={style.cardHeader}>
        <Typhography size="bold-16" style={{ color: "#00212c", margin: 0 }}>
          Profilo load-velocity
        </Typhography>
      </div>

      <div className={style.chartWrapper}>
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 20, right: 30, bottom: 40, left: 50 }}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#e7e7e7"
              vertical={false}
            />
            <XAxis
              type="number"
              dataKey="load"
              name="Carico"
              tick={TICK_STYLE}
              tickLine={false}
              axisLine={false}
              label={{
                value: "Carico (kg)",
                position: "insideBottom",
                offset: -20,
                fill: "#4d4d4d",
                fontSize: 11,
                fontFamily: "'Satoshi', sans-serif",
              }}
            />
            <YAxis
              type="number"
              dataKey="velocity"
              name="Velocità"
              tick={TICK_STYLE}
              tickLine={false}
              axisLine={false}
              label={{
                value: "Velocità (m/s)",
                angle: -90,
                position: "insideLeft",
                fill: "#4d4d4d",
                fontSize: 11,
                fontFamily: "'Satoshi', sans-serif",
              }}
            />
            <Tooltip content={<CustomTooltipContent />} />

            {mvt != null && (
              <ReferenceLine
                y={mvt}
                stroke="#f5b350"
                strokeDasharray="5 5"
                strokeWidth={1.5}
                label={{
                  value: `MVT ${mvt} m/s`,
                  fill: "#f5b350",
                  fontSize: 11,
                  fontFamily: "'Satoshi', sans-serif",
                  position: "insideTopRight",
                }}
              />
            )}

            <Scatter
              name="Punti raccolti"
              data={scatterData}
              fill="#063444"
              stroke="#ffffff"
              strokeWidth={1.5}
              r={6}
            />
          </ScatterChart>
        </ResponsiveContainer>
      </div>

      {summaryLabel && (
        <div className={style.chartFooter}>
          <CustomBadge label={summaryLabel} color="light-blue-dust" />
        </div>
      )}
    </div>
  );
};
